// The town's view (phase D1): the townsfolk as figures from their cards (each NPC's colours swapped in, decision
// 0038's skin-toned faces), placed between sim steps and moved by the figure's motion, an activity's pose looping at
// its spot (the smith's hammer, the merchant's patter, chat in the square); the crowd's animation LOD (@rpg/figure's
// `AnimationLod`, gate D1.5: near figures posed every frame, mid and far ones on stepped, staggered clocks, unseen ones
// not at all, only the near band casting shadow maps, none drawn past the last band) with a pose counter; and the
// cutscenes over it all (gate D1.4): the shot camera sampled from the playing timeline at the frame's time, the
// actors it moves, the poses it plays, the puppets it brings on (the Warden rising), the real figures they stand in for
// hidden while it plays. A view: it reads the sim and never writes it.
import * as THREE from "three";
import { AnimationLod, LOD_FAULTS, buildFigure, createMotion, createPoseLayer, createPoseLibrary, resetTurns, viewTest, type ActionPose, type Attachment, type Figure, type FigureCard, type LodCounters, type Locomotion, type Motion, type PoseLayer } from "@rpg/figure";
import { actorsAt, cameraAt, linesAt, type Placed, type ShotPose } from "@rpg/camera";
import type { LowPolyLook } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { TownMount } from "./scene";

/** The view's planted defects (beside what they break). */
export const TOWN_VIEW_FAULTS = {
  /** Figures out of view are posed every frame (gate D1.5's planted defect: `@rpg/figure`'s `LOD_FAULTS`). */
  offscreenPosedEveryTick: false,
};

/** The poses an activity loops at its spot (agent): the rest stand idle. */
const ACTIVITY_POSES: Record<string, { pose: string; every: number }> = {
  hammer: { pose: "npc.hammer", every: 0 },
  trade: { pose: "npc.talk", every: 5 },
  chat: { pose: "npc.talk", every: 7 },
};

interface Folk {
  id: string;
  fig: Figure;
  motion: Motion;
  layer: PoseLayer;
  prev: { x: number; y: number; z: number; yaw: number };
  curr: { x: number; y: number; z: number; yaw: number };
  vel: [number, number];
  /** When its activity's pose last started (view time). */
  lastPose: number;
  casting: boolean;
  /** Motion updates done (the pose counter's own count). */
  poses: number;
  /** Where a scene put it last frame (its walk's velocity is the change), or null outside a scene. */
  scened: { x: number; z: number } | null;
}

/** What the view reports. */
export interface TownViewReport {
  figures: number;
  shown: number;
  inside: number;
  lod: LodCounters;
  /** Motion updates since the last reset: all, and of figures out of view. */
  poses: { total: number; offscreen: number; frames: number };
  /** The last frame's figures: distance from the camera and whether in view. */
  items: { id: string; distance: number; visible: boolean }[];
  cutscene: { scene: string; t: number; camera: ShotPose | null; lines: string[]; actors: Record<string, Placed> } | null;
  draws: number;
}

export interface TownView {
  report(): TownViewReport;
  resetCounters(): void;
  /** The figure of an NPC or a puppet. */
  figure(id: string): Figure | null;
  /** Build every figure now and stand them round `at` (in the view and the sun's shadow camera) for the warm-up render,
   *  so their skinned surface and depth programs compile before ready; returns the clean-up after that render. */
  warm(at: { x: number; y: number; z: number }): () => void;
  lod: AnimationLod;
}

export function mountTownView(o: {
  runtime: Runtime;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  look: LowPolyLook;
  content: SpikeContent;
  town: TownMount;
  /** Whether the tier casts character shadows at all (the near band's shadows ride on it). */
  tierCasts: boolean;
  /** Cut the follow camera back behind the hero (a scene ended). */
  snapCamera?: () => void;
}): TownView {
  const { town, look } = o;
  const reg = o.content.registry;
  const cards = new Map<string, FigureCard>(reg.rows("cards").map((c) => [c.id, c as unknown as FigureCard]));
  const attachments = new Map(reg.rows("attachments").map((a) => [a.id, a]));
  const library = createPoseLibrary(reg.rows("poses") as unknown as ActionPose[]);
  const lod = new AnimationLod(town.content.lod);
  const folk = new Map<string, Folk>();
  const puppets = new Map<string, { fig: Figure; motion: Motion; layer: PoseLayer; card: string }>();
  const make = (card: string, seed: number, colours: Record<string, string>, name: string): Figure => {
    const c = cards.get(card);
    if (!c) throw new Error(`town view: no card "${card}"`);
    // one draw call a figure (a skinned mesh, a bone per joint): the crowd's draw calls stay inside the phone's column
    const fig = buildFigure(c, { material: look.surface, colour: (k) => look.palette(colours[k] ?? k), seed, single: true });
    fig.root.name = name;
    const att = c.socket ? (c as { attachments?: { id: string }[] }).attachments?.[0]?.id : undefined;
    if (att && attachments.get(att)) fig.attach(attachments.get(att) as unknown as Attachment);
    o.scene.add(fig.root);
    look.track(fig.root, { blob: (reg.get("cards", card) as { blob?: number } | undefined)?.blob });
    return fig;
  };
  const probe = (x: number, z: number): number => town.world.world.heightAt(x, z);
  const ensure = (id: string): Folk => {
    let f = folk.get(id);
    if (f) return f;
    const def = town.folk().def(id);
    const fig = make(def.card, 1000 + [...id].reduce((a, ch) => a * 31 + ch.charCodeAt(0), 7) % 9000, def.colours, `townsfolk:${id}`);
    const motion = createMotion(fig, cards.get(def.card)!.motion!, (x, z) => probe(x, z));
    const n = town.folk().npc(id);
    const at = { x: n.pos[0], y: n.pos[1], z: n.pos[2], yaw: n.yaw };
    fig.root.position.set(at.x, at.y, at.z);
    fig.root.rotation.y = at.yaw;
    motion.reset();
    f = { id, fig, motion, layer: createPoseLayer(fig, library), prev: at, curr: { ...at }, vel: [0, 0], lastPose: -1e9, casting: true, poses: 0, scened: null };
    folk.set(id, f);
    return f;
  };
  const puppet = (actor: string, card: string): { fig: Figure; motion: Motion; layer: PoseLayer; card: string } => {
    let p = puppets.get(actor);
    if (p && p.card === card) return p;
    const fig = make(card, 77, {}, `puppet:${actor}`);
    fig.root.visible = false;
    p = { fig, motion: createMotion(fig, cards.get(card)!.motion!, (x, z) => probe(x, z)), layer: createPoseLayer(fig, library), card };
    puppets.set(actor, p);
    return p;
  };

  // each sim step: where each NPC stands (the frame poses between the last two)
  o.runtime.on(
    "sim",
    () => {
      for (const n of town.folk().state().npcs) {
        const f = folk.get(n.id);
        if (!f) continue;
        f.prev = f.curr;
        f.curr = { x: n.pos[0], y: n.pos[1], z: n.pos[2], yaw: n.yaw };
        f.vel = [n.vel[0], n.vel[1]];
        if (Math.hypot(f.curr.x - f.prev.x, f.curr.z - f.prev.z) > 2) f.prev = { ...f.curr };
      }
    },
    { order: 1e8, name: "town view: states" },
  );

  const counts = { total: 0, offscreen: 0, frames: 0 };
  let frame = 0;
  let hidden: THREE.Object3D[] = [];
  let lastCut: ReturnType<TownView["report"]>["cutscene"] = null;
  let shown = 0;
  let inside = 0;
  let lastItems: { id: string; distance: number; visible: boolean }[] = [];
  const lerpYaw = (a: number, b: number, t: number): number => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;
  const tmp = new THREE.Vector3();

  /** The scene's time this frame (between its last two steps) and its pieces, or null. */
  const sceneNow = (alpha: number) => {
    const cs = town.cutscenes();
    const p = cs.state().playing;
    const d = cs.scene();
    if (!p || !d) return null;
    const t = Math.max(0, Math.min(d.timeline.duration, p.t - (1 - alpha) * o.runtime.clock.dt));
    const moved = actorsAt(d.timeline, t, p.starts, (id) => town.places.mark(id));
    return { p, d, t, moved };
  };

  // the shot camera, after the follow rig (order -10) has placed the camera: a scene's frame overrides it
  let wasPlaying = false;
  o.runtime.on(
    "late",
    (c) => {
      const s = sceneNow(c.alpha);
      if (!s) {
        if (wasPlaying) {
          wasPlaying = false;
          // back to the follow camera with a cut
          o.snapCamera?.();
        }
        lastCut = null;
        return;
      }
      wasPlaying = true;
      const r = town.cutscenes().resolver;
      const shot = cameraAt(s.d.timeline, s.t, s.p.starts, { actor: (id) => s.moved[id] ?? r.actor(id), mark: r.mark });
      if (shot) {
        o.camera.position.set(shot.position.x, shot.position.y, shot.position.z);
        o.camera.up.set(0, 1, 0);
        o.camera.lookAt(shot.target.x, shot.target.y, shot.target.z);
        if (Math.abs(o.camera.fov - shot.fov) > 1e-6) {
          o.camera.fov = shot.fov;
          o.camera.updateProjectionMatrix();
        }
        o.camera.updateMatrixWorld();
        look.focus(tmp.set(shot.target.x, shot.target.y, shot.target.z));
      }
      lastCut = { scene: s.d.id, t: s.t, camera: shot, lines: linesAt(s.d.timeline, s.t).map((l) => l.line), actors: s.moved };
    },
    { order: -5, name: "town view: shot camera" },
  );

  // the townsfolk and the puppets, after the camera (the LOD reads where it is)
  o.runtime.on(
    "late",
    (c) => {
      if (c.mode === "play" && o.runtime.paused) return;
      frame++;
      const st = town.folk().state();
      const s = sceneNow(c.alpha);
      const scenePoses = s ? town.cutscenes().state().poses : [];
      // the puppets a scene brings on; the real figures they stand in for hidden while it plays
      const want = new Set(s ? s.d.puppets.map((p) => p.actor) : []);
      for (const [actor, p] of puppets) if (!want.has(actor)) p.fig.root.visible = false;
      for (const h of hidden) h.visible = true;
      hidden = [];
      if (s)
        for (const pp of s.d.puppets) {
          const p = puppet(pp.actor, pp.card);
          const at = s.moved[pp.actor] ?? town.places.mark(pp.mark);
          if (!at) continue;
          p.fig.root.visible = true;
          p.fig.root.position.set(at.pos[0], at.pos[1], at.pos[2]);
          p.fig.root.rotation.y = at.yaw;
          const cue = [...scenePoses].reverse().find((x) => x.actor === pp.actor);
          resetTurns(p.fig);
          p.motion.update(Math.min(c.dt, 0.1), { speed: 0, velocity: { x: 0, y: 0, z: 0 }, grounded: true, jump: false, glide: false });
          if (cue) p.layer.actionPose(cue.pose, s.t - cue.at);
          p.fig.syncSkin();
          for (const ch of o.scene.children) if (ch.name.startsWith(`figure:${pp.card}`) && ch.visible) {
            ch.visible = false;
            hidden.push(ch);
          }
        }
      // the LOD's plan over every NPC drawn this frame
      const seen = viewTest(o.camera);
      o.camera.getWorldPosition(tmp);
      const cx = tmp.x;
      const cy = tmp.y;
      const cz = tmp.z;
      LOD_FAULTS.offscreenEveryTick = TOWN_VIEW_FAULTS.offscreenPosedEveryTick;
      const items: { id: string; distance: number; visible: boolean }[] = [];
      const places = new Map<string, { x: number; y: number; z: number; yaw: number }>();
      shown = 0;
      inside = 0;
      for (const n of st.npcs) {
        const f = ensure(n.id);
        if (n.mode === "inside" && !s?.moved[n.id]) {
          f.fig.root.visible = false;
          inside++;
          continue;
        }
        const a = c.alpha;
        const placed = s?.moved[n.id];
        const at = placed
          ? { x: placed.pos[0], y: placed.pos[1], z: placed.pos[2], yaw: placed.yaw }
          : { x: f.prev.x + (f.curr.x - f.prev.x) * a, y: f.prev.y + (f.curr.y - f.prev.y) * a, z: f.prev.z + (f.curr.z - f.prev.z) * a, yaw: lerpYaw(f.prev.yaw, f.curr.yaw, a) };
        places.set(n.id, at);
        const distance = Math.hypot(at.x - cx, at.y + 0.8 - cy, at.z - cz);
        items.push({ id: n.id, distance, visible: seen({ x: at.x, y: at.y + 0.8, z: at.z }, 1.1) });
      }
      lastItems = items;
      const plans = lod.plan(frame, c.dt, items);
      counts.frames++;
      const now = s ? s.t : o.runtime.clock.time;
      for (let i = 0; i < plans.length; i++) {
        const plan = plans[i]!;
        const it = items[i]!;
        const f = folk.get(plan.id)!;
        const at = places.get(plan.id)!;
        f.fig.root.visible = plan.shown;
        if (!plan.shown) continue;
        shown++;
        // a jump (a warp, a snap, the warm-up's line-up): no stride across it
        const jumped = Math.hypot(f.fig.root.position.x - at.x, f.fig.root.position.z - at.z) > 2;
        f.fig.root.position.set(at.x, at.y, at.z);
        f.fig.root.rotation.y = at.yaw;
        if (jumped) f.motion.reset();
        const cast = plan.shadows && o.tierCasts;
        if (cast !== f.casting) {
          f.casting = cast;
          f.fig.root.traverse((x) => {
            const m = x as THREE.Mesh;
            if (m.isMesh) m.castShadow = cast;
          });
        }
        if (!plan.pose) continue;
        // a pose: the motion by the time since the last one, then the activity's or the scene's pose on top
        const n = st.npcs.find((x) => x.id === plan.id)!;
        // a scene's actor walks at the rate the scene moves it (its velocity from frame to frame); otherwise the sim's
        const moved = s?.moved[plan.id] ?? null;
        let vel = f.vel;
        if (moved) {
          const last = f.scened;
          vel = last && plan.dt > 0 ? [(moved.pos[0] - last.x) / plan.dt, (moved.pos[2] - last.z) / plan.dt] : [0, 0];
          f.scened = { x: moved.pos[0], z: moved.pos[2] };
        } else f.scened = null;
        const speed = Math.hypot(vel[0], vel[1]);
        const loco: Locomotion = { speed, velocity: { x: vel[0], y: 0, z: vel[1] }, grounded: true, jump: false, glide: false, acting: false, lookAt: null };
        const act = !s && n.activity ? ACTIVITY_POSES[n.activity] : undefined;
        if (act && f.layer.track.entries.length === 0 && now - f.lastPose >= act.every) {
          f.layer.play(act.pose, now);
          f.lastPose = now;
        }
        const cue = s ? [...scenePoses].reverse().find((x) => x.actor === plan.id) : undefined;
        const steps = Math.max(1, Math.ceil(plan.dt / 0.1 - 1e-9));
        for (let k = 0; k < steps; k++) {
          resetTurns(f.fig);
          f.motion.update(plan.dt / steps, loco);
        }
        if (cue) f.layer.actionPose(cue.pose, s!.t - cue.at);
        else f.layer.apply(now);
        f.fig.syncSkin();
        f.poses++;
        counts.total++;
        if (!it.visible) counts.offscreen++;
      }
    },
    { order: 0, name: "town view: townsfolk" },
  );

  return {
    lod,
    figure: (id) => folk.get(id)?.fig ?? puppets.get(id)?.fig ?? null,
    warm(at) {
      for (const n of town.content.npcs) ensure(n.id);
      for (const sc of town.content.scenes.values()) for (const p of sc.puppets) puppet(p.actor, p.card);
      const all = [...[...folk.values()].map((f) => f.fig), ...[...puppets.values()].map((p) => p.fig)];
      all.forEach((fig, i) => {
        const a = (i / all.length) * Math.PI * 2;
        fig.root.position.set(at.x + Math.sin(a) * 2.5, at.y, at.z + Math.cos(a) * 2.5);
        fig.root.visible = true;
        fig.root.updateMatrixWorld(true);
      });
      return () => {
        for (const p of puppets.values()) p.fig.root.visible = false;
      };
    },
    resetCounters() {
      lod.resetCounters();
      counts.total = 0;
      counts.offscreen = 0;
      counts.frames = 0;
    },
    report: () => {
      let draws = 0;
      for (const f of folk.values()) if (f.fig.root.visible) draws += f.fig.stats().draws;
      return { figures: folk.size, shown, inside, lod: lod.counters(), poses: { ...counts }, items: lastItems.map((i) => ({ ...i })), cutscene: lastCut ? structuredClone(lastCut) : null, draws };
    },
  };
}
